import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { DetrixBrowserResourceEntry, DetrixSession } from '../../types';
import { ErrorNarrative } from '../../components/ErrorNarrative';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface BrowserResourceInspectorProps {
  session: DetrixSession;
  entry: DetrixBrowserResourceEntry;
}

export const BrowserResourceInspector: React.FC<BrowserResourceInspectorProps> = ({ session, entry }) => {
  const [sections, setSections] = useState({
    overview: true,
    requestHeaders: true,
    responseHeaders: true,
    timing: true,
  });

  const toggleSection = (section: keyof typeof sections) => {
    setSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const statusColor =
    entry.status >= 200 && entry.status < 300
      ? 'text-[var(--ok)]'
      : entry.status >= 300 && entry.status < 400
        ? 'text-[var(--warn)]'
        : 'text-[var(--error)]';

  const statusLine = [String(entry.status), entry.statusLine].filter(Boolean).join(' ');
  const failed = Boolean(entry.error) || entry.status >= 400;
  const failureMsg =
    typeof entry.error === 'string' ? entry.error : entry.statusLine ?? `HTTP ${entry.status}`;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
      <div className="shrink-0 bg-[var(--bg-panel)]">
        <ErrorNarrative session={session} />
        {failed ? (
          <div className="border-b border-[var(--inspector-error-border)] bg-[var(--inspector-error-bg)] px-3 py-2.5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--inspector-error-title)]">Resource failed</div>
            <p className="mt-1 text-[12px] leading-snug text-[var(--inspector-error-body)]">{failureMsg}</p>
          </div>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div className="border-b p-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded border bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]">
              {entry.resourceType}
            </span>
            <span className="truncate text-[13px] font-semibold text-[var(--text-primary)]" title={entry.url}>
              {entry.url}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className={cn('font-bold', statusColor)}>{statusLine}</span>
            {entry.fromCache ? <span className="font-medium text-[var(--ok)]">(from cache)</span> : null}
          </div>
        </div>

        <div className="min-w-0">
        <CollapsibleSection title="Overview" isOpen={sections.overview} onToggle={() => toggleSection('overview')}>
          <div className="space-y-1 py-1">
            <KVRow label="URL" value={entry.url} valueClass="break-all text-[var(--link)]" />
            <KVRow label="Method" value={entry.method} />
            <KVRow label="Resource Type" value={entry.resourceType} />
            <KVRow label="Chrome Request ID" value={entry.chromeRequestId} />
            {entry.error ? <KVRow label="Error" value={entry.error} valueClass="text-[var(--error)]" /> : null}
          </div>
        </CollapsibleSection>

        {entry.responseHeaders ? (
          <CollapsibleSection
            title="Response Headers"
            isOpen={sections.responseHeaders}
            onToggle={() => toggleSection('responseHeaders')}
          >
            <div className="space-y-1 py-1">
              {Object.entries(entry.responseHeaders)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k, v]) => (
                  <KVRow key={k} label={k} value={v} />
                ))}
            </div>
          </CollapsibleSection>
        ) : null}

        {entry.requestHeaders ? (
          <CollapsibleSection
            title="Request Headers"
            isOpen={sections.requestHeaders}
            onToggle={() => toggleSection('requestHeaders')}
          >
            <div className="space-y-1 py-1">
              {Object.entries(entry.requestHeaders)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k, v]) => (
                  <KVRow key={k} label={k} value={v} />
                ))}
            </div>
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection title="Timing" isOpen={sections.timing} onToggle={() => toggleSection('timing')}>
          <div className="space-y-1 py-1">
            <KVRow label="Start (ms)" value={String(entry.timing.startMs)} />
            <KVRow label="End (ms)" value={String(entry.timing.endMs)} />
            <KVRow label="Duration" value={`${entry.timing.durationMs} ms`} valueClass="font-bold text-[var(--text-primary)]" />
          </div>
        </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

const CollapsibleSection: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, isOpen, onToggle, children }) => (
  <div className="border-b border-[var(--border-subtle)]">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-1.5 transition-colors hover:bg-[var(--bg-elevated)]"
    >
      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <span className="text-[11px] font-bold uppercase text-[var(--text-muted)]">{title}</span>
    </button>
    {isOpen ? <div className="px-3 pb-2">{children}</div> : null}
  </div>
);

const KVRow: React.FC<{ label: string; value: string; valueClass?: string }> = ({ label, value, valueClass }) => (
  <div className="flex gap-2 font-mono text-[11px] leading-relaxed">
    <span className="w-[120px] shrink-0 select-none text-[var(--text-secondary)]">{label}:</span>
    <span className={cn('break-all text-[var(--text-primary)]', valueClass)}>{value}</span>
  </div>
);
